import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Sparkles, ArrowLeft, Eye } from 'lucide-react';
import { toast } from 'sonner';
import BadgeSizeSelector from '../components/BadgeSizeSelector';
import UploadSection from '../components/UploadSection';
import BadgeCanvas from '../components/BadgeCanvas';
import LayoutPreview from '../components/LayoutPreview';
import BatchControls from '../components/BatchControls';
import GridView from '../components/GridView';
import ExportPreview from '../components/ExportPreview';
import { generatePDF } from '../utils/pdfGenerator';

export default function BadgeMakerPage() {
  const navigate = useNavigate();
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadMode, setUploadMode] = useState('single'); // 'single' or 'multiple'
  const [showGuides, setShowGuides] = useState(true);
  const [orientation, setOrientation] = useState('portrait');
  const [viewMode, setViewMode] = useState('single'); // 'single' or 'grid'
  const [selectedBadgeIndex, setSelectedBadgeIndex] = useState(0);
  const [badgeTransforms, setBadgeTransforms] = useState([]);
  const [showExportPreview, setShowExportPreview] = useState(false);
  const [autoCropOnExport, setAutoCropOnExport] = useState(true);
  const canvasRef = useRef(null);

  const handleImageUpload = (files) => {
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      toast.error('Please upload valid image files');
      return;
    }

    const imagePromises = imageFiles.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve({
            id: Math.random().toString(36).substr(2, 9),
            url: e.target.result,
            name: file.name
          });
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(imagePromises).then(images => {
      if (uploadMode === 'single') {
        setUploadedImages([images[0]]);
        setBadgeTransforms([{ zoom: 1, position: { x: 0, y: 0 }, rotation: 0 }]);
        toast.success('Image uploaded successfully!');
      } else {
        setUploadedImages(prev => [...prev, ...images]);
        const newTransforms = images.map(() => ({ zoom: 1, position: { x: 0, y: 0 }, rotation: 0 }));
        setBadgeTransforms(prev => [...prev, ...newTransforms]);
        toast.success(`${images.length} image(s) uploaded successfully!`);
      }
    });
  };

  const handleImageTransformChange = (transform) => {
    setBadgeTransforms(prev => {
      const newTransforms = [...prev];
      newTransforms[selectedBadgeIndex] = transform;
      return newTransforms;
    });
  };

  const handleApplyToAll = () => {
    if (badgeTransforms[selectedBadgeIndex]) {
      const currentTransform = badgeTransforms[selectedBadgeIndex];
      setBadgeTransforms(prev => prev.map(() => ({ ...currentTransform })));
      toast.success('Transform applied to all badges!');
    }
  };

  const handleGeneratePDF = () => {
    setShowExportPreview(true);
  };

  const handleExportConfirm = async () => {
    if (!selectedBadge) {
      toast.error('Please select a badge size first');
      return;
    }

    if (uploadedImages.length === 0) {
      toast.error('Please upload at least one image');
      return;
    }

    try {
      toast.loading('Generating PDF...');
      await generatePDF({
        badge: selectedBadge,
        images: uploadedImages,
        transforms: badgeTransforms,
        orientation,
        showGuides,
        autoCrop: autoCropOnExport
      });
      toast.dismiss();
      toast.success('PDF generated successfully!');
      setShowExportPreview(false);
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to generate PDF. Please try again.');
      console.error('PDF generation error:', error);
    }
  };

  const handleRemoveImage = (imageId) => {
    const index = uploadedImages.findIndex(img => img.id === imageId);
    setUploadedImages(prev => prev.filter(img => img.id !== imageId));
    setBadgeTransforms(prev => prev.filter((_, i) => i !== index));
    if (selectedBadgeIndex >= uploadedImages.length - 1) {
      setSelectedBadgeIndex(Math.max(0, uploadedImages.length - 2));
    }
    toast.info('Image removed');
  };

  const handleClearAll = () => {
    setUploadedImages([]);
    setBadgeTransforms([]);
    setSelectedBadgeIndex(0);
    toast.info('All images cleared');
  };

  // Prepare badge data for grid and export
  const badgeData = uploadedImages.map((image, index) => ({
    image,
    transform: badgeTransforms[index],
    isOverflowing: false // This would be calculated based on transform
  }));

  const currentImage = uploadMode === 'single' 
    ? uploadedImages[0] 
    : uploadedImages[selectedBadgeIndex];

  const hasTransforms = badgeTransforms.some(t => 
    t && (t.zoom !== 1 || t.position.x !== 0 || t.position.y !== 0 || t.rotation !== 0)
  );

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="rounded-xl"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-playful">
                <Sparkles className="w-5 h-5 text-primary-foreground" />
              </div>
              <h1 className="text-2xl font-bold gradient-text">Badge Maker</h1>
            </div>
          </div>
          <Button
            onClick={handleGeneratePDF}
            disabled={!selectedBadge || uploadedImages.length === 0}
            className="rounded-2xl bg-gradient-to-r from-primary to-secondary hover:shadow-hover transition-all duration-300 hover:scale-105"
            size="lg"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview & Export
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Panel - Controls */}
          <div className="lg:col-span-1 space-y-6">
            {/* Step 1: Badge Size */}
            <Card className="card-playful p-6 bg-card border-border/50">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-primary-foreground font-bold text-sm">
                  1
                </div>
                <h3 className="text-lg font-semibold">Choose Badge Size</h3>
              </div>
              <BadgeSizeSelector
                selectedBadge={selectedBadge}
                onSelectBadge={setSelectedBadge}
                orientation={orientation}
                onOrientationChange={setOrientation}
              />
            </Card>

            {/* Step 2: Upload Images */}
            <Card className="card-playful p-6 bg-card border-border/50">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-primary-foreground font-bold text-sm">
                  2
                </div>
                <h3 className="text-lg font-semibold">Upload Images</h3>
              </div>
              <UploadSection
                uploadMode={uploadMode}
                onUploadModeChange={setUploadMode}
                onImageUpload={handleImageUpload}
                uploadedImages={uploadedImages}
                onRemoveImage={handleRemoveImage}
                onClearAll={handleClearAll}
              />
            </Card>

            {/* Step 3: Options */}
            <Card className="card-playful p-6 bg-card border-border/50">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-primary-foreground font-bold text-sm">
                  3
                </div>
                <h3 className="text-lg font-semibold">Display Options</h3>
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Show Cutting Guides</label>
                <Button
                  variant={showGuides ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowGuides(!showGuides)}
                  className="rounded-xl"
                >
                  {showGuides ? 'ON' : 'OFF'}
                </Button>
              </div>
            </Card>
          </div>

          {/* Right Panel - Preview */}
          <div className="lg:col-span-2 space-y-6">
            {/* Batch Controls */}
            {uploadedImages.length > 0 && (
              <BatchControls
                onApplyToAll={handleApplyToAll}
                hasTransforms={hasTransforms}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                badgeCount={uploadedImages.length}
              />
            )}

            {viewMode === 'grid' && uploadedImages.length > 1 ? (
              <Card className="card-playful p-8 bg-card border-border/50 min-h-[600px]">
                <GridView
                  badges={badgeData}
                  onSelectBadge={setSelectedBadgeIndex}
                  selectedIndex={selectedBadgeIndex}
                />
              </Card>
            ) : (
              <Tabs defaultValue="design" className="w-full">
                <TabsList className="grid w-full grid-cols-2 rounded-2xl">
                  <TabsTrigger value="design" className="rounded-xl">Design View</TabsTrigger>
                  <TabsTrigger value="layout" className="rounded-xl">A4 Layout</TabsTrigger>
                </TabsList>
                
                <TabsContent value="design" className="mt-6">
                  <Card className="card-playful p-8 bg-card border-border/50 min-h-[600px]">
                    <BadgeCanvas
                      ref={canvasRef}
                      badge={selectedBadge}
                      image={currentImage}
                      showGuides={showGuides}
                      orientation={orientation}
                      onImageTransformChange={handleImageTransformChange}
                    />
                  </Card>
                </TabsContent>
                
                <TabsContent value="layout" className="mt-6">
                  <Card className="card-playful p-8 bg-card border-border/50 min-h-[600px]">
                    <LayoutPreview
                      badge={selectedBadge}
                      images={uploadedImages}
                      orientation={orientation}
                      uploadMode={uploadMode}
                    />
                  </Card>
                </TabsContent>
              </Tabs>
            )}
          </div>
        </div>
      </div>

      {/* Export Preview Modal */}
      <ExportPreview
        isOpen={showExportPreview}
        onClose={() => setShowExportPreview(false)}
        badgeData={badgeData}
        onExport={handleExportConfirm}
        autoCropEnabled={autoCropOnExport}
        onToggleAutoCrop={() => setAutoCropOnExport(!autoCropOnExport)}
      />
    </div>
  );
}
